import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { CreditCard, Loader2 } from 'lucide-react';
import { WaIcon } from './Header';
import { TALLAS, WHATSAPP, MINIMO_KG, fmtCOP, ZONAS } from '../mock';
import { useToast } from '../hooks/use-toast';
import { trackWhatsAppClick, trackPedidoEnviado } from '../analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OrderBuilder = () => {
  const [kilos, setKilos] = useState({ Grande: 3, Mediana: 0, 'Pequeña': 0 });
  const [nombre, setNombre] = useState('');
  const [direccion, setDireccion] = useState('');
  const [ciudad, setCiudad] = useState('');
  const [telefono, setTelefono] = useState('');
  const [zona, setZona] = useState('barranquilla');
  const [pagando, setPagando] = useState(false);
  const { toast } = useToast();

  const setKg = (t, v) => {
    let n = parseInt(v, 10);
    if (isNaN(n) || n < 0) n = 0;
    n = Math.min(n, 10000);
    setKilos((k) => ({ ...k, [t]: n }));
  };

  const { lineas, total, totalKg } = useMemo(() => {
    let total = 0, totalKg = 0;
    const lineas = [];
    TALLAS.forEach((t) => {
      const kg = kilos[t.id] || 0;
      if (kg > 0) {
        const sub = kg * t.precio;
        total += sub;
        totalKg += kg;
        lineas.push({ id: t.id, kg, precio: t.precio, sub, descLarga: t.descLarga });
      }
    });
    return { lineas, total, totalKg };
  }, [kilos]);

  const zonaSel = useMemo(() => ZONAS.find((z) => z.id === zona) || ZONAS[0], [zona]);
  const domicilio = zonaSel.fee;
  const totalConDomicilio = total + (totalKg >= MINIMO_KG ? domicilio : 0);

  const cumpleMinimo = totalKg >= MINIMO_KG;

  const waHref = useMemo(() => {
    if (!cumpleMinimo) return undefined;
    let msg = 'Hola Finca Pescado, quiero hacer un pedido de cachama eviscerada:';
    lineas.forEach((l) => {
      msg += `\n- ${l.id} (${l.descLarga}): ${l.kg} kg × ${fmtCOP(l.precio)} = ${fmtCOP(l.sub)}`;
    });
    msg += `\n- Subtotal: ${totalKg} kg — ${fmtCOP(total)}`;
    msg += `\n- Domicilio (${zonaSel.label}): ${domicilio > 0 ? fmtCOP(domicilio) : 'Gratis'}`;
    msg += `\n- Total: ${fmtCOP(totalConDomicilio)} (a confirmar al pesar)`;
    if (nombre.trim()) msg += `\n- Nombre: ${nombre.trim()}`;
    if (telefono.trim()) msg += `\n- Teléfono: ${telefono.trim()}`;
    const entrega = [direccion.trim(), ciudad.trim()].filter(Boolean).join(', ');
    if (entrega) msg += `\n- Entrega: ${entrega}`;
    return `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(msg)}`;
  }, [lineas, total, totalKg, nombre, direccion, ciudad, telefono, cumpleMinimo, zonaSel, domicilio, totalConDomicilio]);

  // Pago real con Stripe Checkout (COP)
  const pagarConTarjeta = async () => {
    if (!cumpleMinimo || pagando) return;
    setPagando(true);
    try {
      const { data } = await axios.post(`${API}/orders/checkout`, {
        kilos,
        nombre: nombre.trim(),
        direccion: direccion.trim(),
        ciudad: ciudad.trim(),
        telefono: telefono.trim(),
        zona,
        origin_url: window.location.origin,
      });
      if (data.checkout_url) {
        trackPedidoEnviado('stripe');
        window.location.href = data.checkout_url;
      } else {
        throw new Error('Sin URL de pago');
      }
    } catch (e) {
      setPagando(false);
      const detalle = e?.response?.data?.detail;
      toast({
        title: 'No se pudo iniciar el pago',
        description: typeof detalle === 'string' ? detalle : 'Intenta de nuevo o envía tu pedido por WhatsApp.',
        variant: 'destructive',
      });
    }
  };

  return (
    <section className="pedido" id="pedido">
      <div className="wrap">
        <p className="eyebrow">Compra directa de la finca</p>
        <h2 className="sec">Arma tu pedido por kilos</h2>
        <p className="sec-sub">
          Elige cuántos kilos quieres de cada tamaño — puedes combinar. Puedes enviar tu pedido por WhatsApp o pagar en
          línea con tarjeta — nosotros confirmamos el total exacto al pesarlo y coordinamos la entrega.
        </p>
        <div className="pedido-grid">
          <div>
            <label className="lbl">Nuestro producto</label>
            <div className="card-prod sel" style={{ cursor: 'default' }}>
              <span>
                <strong>Cachama eviscerada</strong>
                <br />
                <small style={{ color: 'var(--tinta-2)' }}>Limpia y lavada en planta el mismo día de la cosecha, lista para cocinar</small>
              </span>
              <span className="pr">
                <span className="precio">$6.000–$10.000</span>
                <br />
                <small style={{ color: 'var(--tinta-2)' }}>por kilo, según tamaño*</small>
              </span>
            </div>

            <label className="lbl">1 · ¿Cuántos kilos de cada tamaño?</label>
            <div style={{ display: 'grid', gap: 12 }}>
              {TALLAS.map((t) => (
                <div className="talla-card" key={t.id}>
                  <img src={t.img} alt={t.alt} loading="lazy" />
                  <span className="tinfo">
                    <strong>{t.nombre}</strong>
                    <small>{t.desc}</small>
                    <span className="tprecio">{fmtCOP(t.precio)}/kg</span>
                  </span>
                  <div className="stepper" data-talla={t.id}>
                    <button type="button" aria-label={`Quitar un kilo de ${t.nombre.toLowerCase()}`} onClick={() => setKg(t.id, (kilos[t.id] || 0) - 1)}>−</button>
                    <div className="kg">
                      <input
                        className="t-kg"
                        type="number"
                        inputMode="numeric"
                        min="0"
                        max="10000"
                        step="1"
                        value={kilos[t.id]}
                        aria-label={`Kilos de ${t.nombre.toLowerCase()}`}
                        onChange={(e) => setKg(t.id, e.target.value)}
                      />
                      <small>kg</small>
                    </div>
                    <button type="button" aria-label={`Agregar un kilo de ${t.nombre.toLowerCase()}`} onClick={() => setKg(t.id, (kilos[t.id] || 0) + 1)}>+</button>
                  </div>
                </div>
              ))}
            </div>
            <p style={{ fontSize: '.85rem', color: 'var(--tinta-2)', marginTop: 10 }}>
              Puedes combinar tamaños. Pedido mínimo a domicilio: 2 kg en total. ¿Restaurante o evento? Escríbenos por mayor.
            </p>

            <label className="lbl" htmlFor="nombre">2 · Tus datos de entrega</label>
            <div style={{ display: 'grid', gap: 12 }}>
              <input className="field" id="nombre" placeholder="Tu nombre" autoComplete="name" value={nombre} onChange={(e) => setNombre(e.target.value)} />
              <input className="field" id="telefono" placeholder="Teléfono / WhatsApp" autoComplete="tel" value={telefono} onChange={(e) => setTelefono(e.target.value)} />
              <input className="field" id="direccion" placeholder="Dirección de entrega" autoComplete="street-address" value={direccion} onChange={(e) => setDireccion(e.target.value)} />
              <input className="field" id="ciudad" placeholder="Ciudad o municipio (ej. Barranquilla)" autoComplete="address-level2" value={ciudad} onChange={(e) => setCiudad(e.target.value)} />
            </div>

            <label className="lbl">3 · ¿Dónde entregamos tu pedido?</label>
            <div style={{ display: 'grid', gap: 10 }}>
              {ZONAS.map((z) => (
                <div
                  key={z.id}
                  className={`card-prod zona-card${zona === z.id ? ' sel' : ''}`}
                  role="radio"
                  aria-checked={zona === z.id}
                  tabIndex={0}
                  onClick={() => setZona(z.id)}
                  onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setZona(z.id)}
                  data-testid={`zona-${z.id}`}
                >
                  <span><strong>{z.label}</strong><br /><small style={{ color: 'var(--tinta-2)' }}>{z.nota}</small></span>
                  <span className="pr"><span className="precio" style={{ fontSize: '1.1rem' }}>{z.fee > 0 ? fmtCOP(z.fee) : 'Gratis'}</span></span>
                </div>
              ))}
            </div>
          </div>

          <aside className="resumen" aria-live="polite">
            <h3>Tu pedido</h3>
            <div className="res-line"><span>Producto</span><span>Cachama eviscerada</span></div>
            {lineas.length > 0 ? (
              lineas.map((l) => (
                <div className="res-line" key={l.id}>
                  <span>{l.id} · {l.kg} kg × {fmtCOP(l.precio)}</span>
                  <span>{fmtCOP(l.sub)}</span>
                </div>
              ))
            ) : (
              <div className="res-line"><span>Elige los kilos de cada tamaño</span><span></span></div>
            )}
            <div className="res-line"><span>Total kilos</span><span>{totalKg} kg</span></div>
            <div className="res-line"><span>Subtotal productos</span><span>{fmtCOP(total)}</span></div>
            <div className="res-line"><span>Domicilio · {zonaSel.label}</span><span>{domicilio > 0 ? fmtCOP(domicilio) : 'Gratis'}</span></div>
            <div className="res-total"><span>Total estimado</span><span>{fmtCOP(totalConDomicilio)}</span></div>
            {zona === 'barranquilla' && (
              <p className="res-nota" style={{ marginBottom: 0, fontWeight: 700, color: 'var(--rio)' }}>
                Entrega en Barranquilla: martes y sábados (ruta agrupada). Te confirmamos la franja por WhatsApp.
              </p>
            )}
            <p className="res-nota">*Precio de referencia. El total exacto se confirma al pesar tu pedido. El domicilio de tu zona ya está incluido en el total.</p>
            {cumpleMinimo ? (
              <a
                className="btn btn-wa"
                href={waHref}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => { trackWhatsAppClick('pedido'); trackPedidoEnviado('whatsapp'); }}
              ><WaIcon /> Enviar pedido por WhatsApp</a>
            ) : (
              <span className="btn btn-wa off">Pedido mínimo: {MINIMO_KG} kg en total</span>
            )}
            <button
              type="button"
              className={`btn btn-stripe${!cumpleMinimo ? ' off' : ''}`}
              onClick={pagarConTarjeta}
              disabled={!cumpleMinimo || pagando}
              data-testid="btn-pagar-tarjeta"
            >
              {pagando ? <Loader2 size={20} className="animate-spin" /> : <CreditCard size={20} />}
              {pagando ? ' Procesando...' : ' Pagar con tarjeta (COP)'}
            </button>
            <p className="stripe-nota">Pago seguro con tarjeta en pesos colombianos (COP) a través de Stripe.</p>
          </aside>
        </div>
      </div>
    </section>
  );
};

export default OrderBuilder;